/**
 * Chat's list in the sidebar (AppNav.jsx): New chat, then the recent chats.
 * ChatConsole publishes the chat rows to dockStore through chatBar.
 */
import { useEffect, useState } from "react";
import ScrollArea from "./ScrollArea.jsx";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { NavListLabel, NavNewRow } from "./sidebarPrimitives.jsx";
import { formatRelativeTime } from "./skills/skillBadges.jsx";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE, DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import { useWorkspaceChats } from "../lib/traceCanvasStore.js";

export default function CanvasMenu({
    layout = "hub",
    // False when the caller lacks the chat's RBAC permission — drops the
    // whole Recents section (history + New chat) so no affordance points at
    // a console that isn't mounted.
    chatEnabled = true,
    chatBar = null,
    chatActive = false,
    onOpenChat,
    onNewChat,
}) {
    // History = started chats only (blank drafts don't earn an entry until
    // their first message), and only the main chat's: one begun in a
    // workspace is that workspace's.
    const workspaceChats = useWorkspaceChats();
    const startedChats = (chatBar?.tabs || []).filter((t) => t.hasItems && !workspaceChats.has(String(t.id)));
    const [deleteId, setDeleteId] = useState(null);
    const pendingDelete = startedChats.find((t) => t.id === deleteId);

    // Coarse re-render tick so the relative-time notes age in place —
    // they're a pure render of chatBar, which is only republished when a
    // tab's identity fields change, so "just now" would otherwise stay
    // "just now" indefinitely.
    const [, setClockTick] = useState(0);
    useEffect(() => {
        if (!startedChats.length) return undefined;
        const t = setInterval(() => setClockTick((n) => n + 1), 60_000);
        return () => clearInterval(t);
    }, [startedChats.length]);

    return (
        <nav
            data-admin-menu
            className="flex h-full min-h-0 min-w-0 flex-col"
            aria-label="Chat navigation"
        >
            {layout === "hub" && chatEnabled ? (
                <>
                    <NavNewRow
                        data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, ACTION.NEW_CHAT)}
                        onClick={() => onNewChat?.()}
                        data-drawer-pick=""
                    >
                        New chat
                    </NavNewRow>
                    <NavListLabel>Recents</NavListLabel>
                    <ScrollArea
                        data-sidebar-scroll
                        className="min-h-0 flex-1"
                        contentClassName="grid gap-[2px] pb-[12px]"
                    >
                        {startedChats.map((t) => (
                            <div key={t.id} className="group relative">
                                <button
                                    type="button"
                                    aria-current={chatActive && t.active ? "page" : undefined}
                                    onClick={() => onOpenChat?.(t.id)}
                                    data-drawer-pick=""
                                    className={`grid w-full gap-[1px] rounded-[8px] py-[7px] pl-[9.5px] pr-[32px] text-left transition-colors ${chatActive && t.active ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:bg-[color-mix(in_srgb,var(--side-raise)_55%,transparent)] hover:text-[color:var(--ink)]"}`}
                                >
                                    <span className="truncate text-[13px] leading-[18px]">{t.label || "New chat"}</span>
                                    {t.updatedAt ? <span className="text-[11px] leading-[15px] tabular-nums text-[color:var(--muted-soft)]">{formatRelativeTime(t.updatedAt)}</span> : null}
                                </button>
                                {t.onDelete ? <button
                                    type="button"
                                    aria-label={`Delete chat: ${t.label || "New chat"}`}
                                    title="Delete chat"
                                    onClick={() => setDeleteId(t.id)}
                                    className="absolute right-[2px] top-[6px] grid h-[28px] w-[28px] place-items-center rounded-[6px] text-[color:var(--muted)] opacity-0 transition-opacity hover:text-[color:var(--severity-critical)] group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 pointer-coarse:h-[44px] pointer-coarse:w-[32px] pointer-coarse:opacity-100"
                                >
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className="h-[14px] w-[14px]" aria-hidden="true"><path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" /></svg>
                                </button> : null}
                            </div>
                        ))}
                    </ScrollArea>
                </>
            ) : null}
            <ConfirmDialog
                open={!!pendingDelete}
                analyticsId={DIALOG.CHAT_DELETE}
                title="Delete chat?"
                body={`Delete "${pendingDelete?.label || "New chat"}" from your saved chats?`}
                confirmLabel="Delete chat"
                destructive
                onCancel={() => setDeleteId(null)}
                onConfirm={() => { pendingDelete?.onDelete?.(); setDeleteId(null); }}
            />
        </nav>
    );
}
